import { getApiBaseUrl } from "../config/runtimeConfig";
import { invalidateLookup } from "../lookups/lookupsApi";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export interface Subcontractor {
  id: number;
  name: string;
  taxId?: string | null;
  status?: string | null;
  contactName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  projectsCount: number;
  notes?: string | null;
  logoUrl?: string | null;
  specialties: SubcontractorSpecialty[];
  created: string;
  lastModified: string;
}

export interface SubcontractorSpecialty {
  id: number;
  name: string;
}

export interface SubcontractorPayload {
  name: string;
  taxId?: string | null;
  specialtyIds?: number[];
  status?: string | null;
  contactName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  projectsCount?: number;
  notes?: string | null;
}

export interface SubcontractorLogoResponse {
  subcontractorId: number;
  logoUrl?: string | null;
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(
      response.status,
      text || response.statusText,
    );
  }

  return (await response.json()) as T;
}

export function getSubcontractors(): Promise<Subcontractor[]> {
  return request<Subcontractor[]>("/api/subcontractors");
}

export function getSubcontractorSpecialties(): Promise<
  SubcontractorSpecialty[]
> {
  return request<SubcontractorSpecialty[]>(
    "/api/subcontractor-specialties",
  );
}

export function getSubcontractorById(
  subcontractorId: number,
): Promise<Subcontractor> {
  return request<Subcontractor>(
    `/api/subcontractors/${subcontractorId}`,
  );
}

export function createSubcontractor(
  payload: SubcontractorPayload,
): Promise<Subcontractor> {
  return request<Subcontractor>("/api/subcontractors", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  }).then((result) => {
    invalidateLookup("subcontractors");
    return result;
  });
}

export function updateSubcontractor(
  subcontractorId: number,
  payload: SubcontractorPayload,
): Promise<Subcontractor> {
  return request<Subcontractor>(
    `/api/subcontractors/${subcontractorId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  ).then((result) => {
    invalidateLookup("subcontractors");
    return result;
  });
}

export function uploadSubcontractorLogo(
  subcontractorId: number,
  file: File,
): Promise<SubcontractorLogoResponse> {
  const formData = new FormData();
  formData.append("logo", file, file.name || "logo");

  return request<SubcontractorLogoResponse>(
    `/api/subcontractors/${subcontractorId}/logo`,
    {
      method: "POST",
      body: formData,
    },
  );
}
